import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { propertyData } from '../mockData';
import { ChevronDown } from 'lucide-react';
import CTAButtons from './CTAButtons';

// User's custom Skopelos timelapse video
const HERO_VIDEO_URL = "https://customer-assets.emergentagent.com/job_aegean-property/artifacts/jyxg9w30_Create%20%C2%B7%20Nim%20skopelos%20timlapse.mp4";

const Hero = () => {
  const { language } = useLanguage();
  const content = (propertyData[language] || propertyData.en).hero;
  const [videoError, setVideoError] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const videoRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Ensure video plays on mobile
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {
        // Autoplay was prevented, video will show first frame
        console.log('Video autoplay prevented');
      });
    }
  }, [videoLoaded]);

  return (
    <section 
      className="relative h-screen flex items-center justify-center overflow-hidden"
      aria-label="Hero section showcasing Skopelos property"
      itemScope 
      itemType="https://schema.org/RealEstateListing"
    >
      {/* Video Background with Parallax */}
      {!videoError && (
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          onError={() => setVideoError(true)}
          onLoadedData={() => setVideoLoaded(true)}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-100"
          style={{
            transform: `scale(1.1) translate(${mousePosition.x * 0.3}px, ${mousePosition.y * 0.3}px)`
          }}
        >
          <source src={HERO_VIDEO_URL} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      )}

      {/* Fallback Background Image (shown if video fails or during loading) */}
      {(videoError || !videoLoaded) && (
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-transform duration-100"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1759668559041-5925eb01cd83?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxHcmVlayUyMGlzbGFuZCUyMGNvdHRhZ2V8ZW58MHx8fHwxNzY4OTAyMjMyfDA&ixlib=rb-4.1.0&q=85)',
            transform: `scale(1.1) translate(${mousePosition.x * 0.3}px, ${mousePosition.y * 0.3}px)`
          }}
        />
      )}

      {/* Dark Overlay with Gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
      
      {/* Content with 3D Text Effect */}
      <div 
        className="relative z-10 text-center text-white px-4 sm:px-6 max-w-5xl transition-transform duration-100"
        style={{
          transform: `perspective(1000px) rotateX(${mousePosition.y * 0.05}deg) rotateY(${mousePosition.x * 0.05}deg)`
        }}
      >
        <h1 
          className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-light mb-4 sm:mb-6 tracking-wide leading-tight"
          style={{
            textShadow: '0 10px 30px rgba(0,0,0,0.5), 0 0 60px rgba(255,255,255,0.1)',
            transform: `translateZ(50px)`
          }}
          itemProp="name"
        >
          {content.title}
        </h1>
        <p 
          className="text-sm sm:text-base md:text-lg lg:text-2xl font-light mb-8 sm:mb-12 tracking-wide opacity-95 px-2"
          style={{
            textShadow: '0 5px 20px rgba(0,0,0,0.5)',
            transform: `translateZ(30px)`
          }}
          itemProp="description"
        >
          {content.subtitle}
        </p>
        <CTAButtons variant="hero" />
      </div>

      {/* Scroll Down Indicator */}
      <button
        onClick={() => document.getElementById('overview').scrollIntoView({ behavior: 'smooth' })}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white animate-bounce hover:scale-125 transition-transform duration-300"
        aria-label="Scroll to content"
      >
        <ChevronDown className="w-8 h-8" />
      </button>
    </section>
  );
};

export default Hero;